import { memo, useCallback, useMemo, useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useChatStore } from "@/stores/chat-store";
import { useThreadStore } from "@/stores/thread-store";
import { useChat } from "@/hooks/useChat";
import { useThreadActions } from "@/hooks/useThreads";
import { useConcept, useRefreshSuggestedQuestions } from "@/hooks/useConcepts";
import { guideSignals } from "@/lib/guideSignals";
import { ThreadSidebar } from "./ThreadSidebar";
import { CitationText } from "./CitationText";
import type { SourceAttribution } from "@/types/paper";
import Markdown from "react-markdown";
import { toast } from "@/lib/toast";
import {
  GitBranch,
  MessageSquare,
  PanelLeftOpen,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";

const SUGGESTED_QUESTIONS = [
  "What are the main themes across these papers?",
  "What methodologies are most commonly used?",
  "What are the key findings and contributions?",
  "Are there any conflicting results or viewpoints?",
  "What research gaps do these papers identify?",
  "How do these papers relate to each other?",
  "What are the limitations discussed?",
  "What future research directions are suggested?",
];

const SOURCE_PRIORITY: Record<string, number> = { debate: 0, corpus: 1, refinement: 2 };

interface Props {
  conceptId: string;
  paperIds?: string[];
}

export const ChatPanel = memo(function ChatPanel({ conceptId, paperIds }: Props) {
  const messages = useChatStore((s) => s.messages);
  const isStreaming = useChatStore((s) => s.isStreaming);
  const sources = useChatStore((s) => s.sources);
  const sourcesMap = useChatStore((s) => s.sourcesMap);
  const error = useChatStore((s) => s.error);
  const activeThreadId = useThreadStore((s) => s.activeThreadId);
  const sidebarOpen = useThreadStore((s) => s.sidebarOpen);
  const { sendMessage, clearChat } = useChat(conceptId, paperIds);
  const { branchThread } = useThreadActions(conceptId);
  const { data: concept } = useConcept(conceptId);
  const refreshMutation = useRefreshSuggestedQuestions(conceptId);
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const displayQuestions = useMemo(() => {
    const custom = concept?.suggested_questions;
    if (custom && custom.length >= 6) {
      return [...custom].sort(
        (a, b) => (SOURCE_PRIORITY[a.source] ?? 9) - (SOURCE_PRIORITY[b.source] ?? 9),
      );
    }
    const sorted = custom
      ? [...custom].sort(
          (a, b) => (SOURCE_PRIORITY[a.source] ?? 9) - (SOURCE_PRIORITY[b.source] ?? 9),
        )
      : [];
    const remaining = SUGGESTED_QUESTIONS
      .filter((q) => !sorted.some((s) => s.text === q))
      .slice(0, 6 - sorted.length)
      .map((text) => ({ text, source: "generic" as const }));
    return [...sorted, ...remaining];
  }, [concept?.suggested_questions]);

  const prevQuestionsRef = useRef(concept?.suggested_questions);
  useEffect(() => {
    const prev = prevQuestionsRef.current;
    const curr = concept?.suggested_questions;
    if (curr && curr !== prev && prev !== undefined) {
      const hasDebate = curr.some((q) => q.source === "debate");
      const hadDebate = prev ? prev.some((q) => q.source === "debate") : false;
      if (hasDebate && !hadDebate) {
        toast.success("Research questions updated with debate insights");
      } else {
        toast.success("Suggested research questions updated");
      }
    }
    prevQuestionsRef.current = curr;
  }, [concept?.suggested_questions]);

  // Auto-scroll to bottom only when message count changes (not on every store update)
  const messageCount = messages.length;
  useEffect(() => {
    if (messageCount > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messageCount]);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const trimmed = input.trim();
      if (!trimmed || isStreaming) return;
      setInput("");
      sendMessage(trimmed);
    },
    [input, isStreaming, sendMessage],
  );

  const handleSuggestedClick = useCallback(
    (question: string) => {
      if (isStreaming) return;
      guideSignals.mark(conceptId, 'hasUsedDynamicChat');
      setInput("");
      sendMessage(question);
    },
    [isStreaming, sendMessage, conceptId],
  );

  const handleBranch = useCallback(
    (messageId: string) => {
      if (!activeThreadId) return;
      branchThread.mutate({
        threadId: activeThreadId,
        data: { message_id: messageId },
      });
    },
    [activeThreadId, branchThread],
  );

  return (
    <div className="flex h-full">
      {/* Thread sidebar */}
      <ThreadSidebar conceptId={conceptId} />

      {/* Chat content */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-2">
          <div className="flex items-center gap-2 text-sm font-medium">
            {!sidebarOpen && (
              <Button
                variant="ghost"
                size="icon-xs"
                onClick={() => useThreadStore.getState().toggleSidebar()}
                aria-label="Open thread sidebar"
              >
                <PanelLeftOpen className="size-3.5" />
              </Button>
            )}
            <MessageSquare className="size-4" />
            Chat with Papers
          </div>
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="xs"
              onClick={clearChat}
              aria-label="Start new chat"
            >
              <Trash2 className="size-3" />
              New Chat
            </Button>
          )}
        </div>

        {/* Messages */}
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {messages.length === 0 && !isStreaming && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Ask questions about the indexed papers. The AI will cite specific
                papers in its response.
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-muted-foreground">Suggested Questions</h4>
                  <button
                    type="button"
                    onClick={() => {
                      refreshMutation.mutate(undefined, {
                        onSuccess: () => toast.success("Research questions refreshed"),
                        onError: () => toast.error("Failed to generate research questions"),
                      });
                    }}
                    disabled={refreshMutation.isPending}
                    title="Refresh suggested questions from your papers"
                    className="rounded p-1 text-muted-foreground hover:bg-muted/50 hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`size-3.5 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-muted-foreground/70">
                  <span className="inline-flex items-center">
                    <span className="mr-1 inline-block size-1.5 rounded-full bg-green-400" />
                    From your Field Debate
                  </span>
                  <span className="inline-flex items-center">
                    <span className="mr-1 inline-block size-1.5 rounded-full bg-amber-400" />
                    From your corpus
                  </span>
                  <span className="inline-flex items-center">
                    <span className="mr-1 inline-block size-1.5 rounded-full bg-blue-400" />
                    About your concept
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {displayQuestions.map((q, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => handleSuggestedClick(q.text)}
                      className="rounded-lg border border-border/50 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
                    >
                      {q.source !== "generic" && (
                        <span
                          className={`mr-1.5 inline-block size-1.5 rounded-full ${
                            q.source === "debate"
                              ? "bg-green-400"
                              : q.source === "corpus"
                                ? "bg-amber-400"
                                : "bg-blue-400"
                          }`}
                        />
                      )}
                      {q.text}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((msg, i) => {
            const msgSources = sourcesMap[i] ?? [];
            const hasSources = msgSources.length > 0;
            const canBranch =
              !isStreaming && activeThreadId && msg.id;

            return (
              <div
                key={i}
                className={`group flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                    msg.role === "user"
                      ? "bg-primary text-primary-foreground whitespace-pre-wrap"
                      : "bg-muted"
                  }`}
                >
                  {msg.role === "assistant" && hasSources && !isStreaming ? (
                    <CitationText content={msg.content} sources={msgSources} />
                  ) : msg.role === "assistant" &&
                    sources.length > 0 &&
                    i === messages.length - 1 &&
                    !isStreaming ? (
                    <CitationText content={msg.content} sources={sources} />
                  ) : msg.role === "assistant" ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                      <Markdown>{msg.content}</Markdown>
                    </div>
                  ) : (
                    msg.content
                  )}
                  {isStreaming &&
                    i === messages.length - 1 &&
                    msg.role === "assistant" && (
                      <span className="animate-pulse">|</span>
                    )}
                </div>

                {/* Branch button */}
                {canBranch && (
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    className="ml-1 self-center opacity-0 group-hover:opacity-100"
                    onClick={() => handleBranch(msg.id!)}
                    aria-label="Branch from this message"
                  >
                    <GitBranch className="size-3" />
                  </Button>
                )}
              </div>
            );
          })}

          {/* Sources for the latest exchange */}
          {sources.length > 0 && !isStreaming && (
            <SourcesList sources={sources} />
          )}

          {/* Error */}
          {error && <p className="text-sm text-destructive">{error}</p>}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSubmit} className="border-t p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                  e.preventDefault();
                  if (input.trim() && !isStreaming) {
                    setInput("");
                    sendMessage(input.trim());
                  }
                }
              }}
              placeholder="Ask about your papers... (Ctrl+Enter to send)"
              disabled={isStreaming}
              className="flex-1 rounded-md border bg-background px-3 py-2 text-sm outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50"
            />
            <Button
              type="submit"
              size="sm"
              disabled={!input.trim() || isStreaming}
              aria-label="Send message"
            >
              <Send className="size-4" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
});

function SourcesList({ sources }: { sources: SourceAttribution[] }) {
  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-xs font-medium text-muted-foreground">Sources</p>
      {sources.map((s) => (
        <div key={s.number} className="text-xs text-muted-foreground">
          <span className="font-medium">[{s.number}]</span> {s.authors} (
          {s.year ?? "n.d."}) &ldquo;{s.title}&rdquo;
        </div>
      ))}
    </div>
  );
}
